import { useStytchB2BClient } from '@stytch/react/b2b';
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { PageSpinner } from '@/components/loaders';
import { sessionDurationMinutes } from '@/constants/numbers';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import { consumePendingSignInMethod, setPreferredSignInMethod } from '@/helper/user-preferences';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import ResetPasswordDiscovery from './ResetPasswordDiscovery';
import { type DiscoveryInfo } from './type';

/**
 * Wraps the Login page and looks for any search params on the URL placed by Stytch after a
 * redirect. If it finds them, it can either redirect to a different url or render the contents of
 * the resulting form.
 *
 * If no Stytch search params are found on the URL, this component passes through `children`
 * instead.
 *
 * For tenant-specific auth flows (when Stytch includes `slug` param), this redirects to
 * `/sign-in/inst/:slug` where TenantAuthenticate handles the token exchange.
 */
const DiscoveryAuthenticate = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const stytch = useStytchB2BClient();
  const hasRun = useRef(false);
  const [discoveryInfo, setDiscoveryInfo] = useState<DiscoveryInfo | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordResetToken, setPasswordResetToken] = useState<string | undefined>();

  // Check if this is a tenant-specific auth callback (Stytch includes slug for org-based flows)
  const slug = searchParams.get('slug');

  const commitPreferredSignInMethod = useCallback(() => {
    const pendingMethod = consumePendingSignInMethod();
    if (pendingMethod) {
      setPreferredSignInMethod(pendingMethod);
    }
  }, []);

  const tokenType = searchParams.get('stytch_token_type');
  const token = searchParams.get('token');
  const didComeFromSignUp = searchParams.get('signup') === 'true';
  const redirectUrl = searchParams.get('redirect');
  const redirectType = searchParams.get('stytch_redirect_type');

  const clearURLSearchParams = useCallback(() => {
    navigate(location.pathname, { replace: true });
  }, [navigate]);

  const redirectAuthenticatedUser = useCallback(() => {
    if (redirectUrl) {
      navigate(redirectUrl, { replace: true });
    } else {
      navigate(ROUTES.app.path, { replace: true });
    }
  }, [redirectUrl, navigate]);

  const handleDiscovery = useCallback(
    async (token: string) => {
      try {
        const { intermediate_session_token, email_address, discovered_organizations } =
          await stytch.magicLinks.discovery.authenticate({
            discovery_magic_links_token: token,
          });

        setDiscoveryInfo({
          intermediateToken: intermediate_session_token,
          email: email_address,
          discoveredOrgs: discovered_organizations,
          isDiscoverySignUp: didComeFromSignUp,
          redirectUrl,
        });
        commitPreferredSignInMethod();
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e),
        });
      }
    },
    [stytch, pushNotification, didComeFromSignUp, redirectUrl, commitPreferredSignInMethod],
  );

  const handleOAuthDiscovery = useCallback(
    async (token: string) => {
      try {
        const { intermediate_session_token, email_address, discovered_organizations } =
          await stytch.oauth.discovery.authenticate({
            discovery_oauth_token: token,
          });
        setDiscoveryInfo({
          intermediateToken: intermediate_session_token,
          email: email_address,
          discoveredOrgs: discovered_organizations,
          isDiscoverySignUp: didComeFromSignUp,
          redirectUrl,
        });
        commitPreferredSignInMethod();
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [stytch, pushNotification, didComeFromSignUp, redirectUrl, commitPreferredSignInMethod],
  );

  const handleMultiTenantMagicLinks = useCallback(
    async (token: string) => {
      try {
        await stytch.magicLinks.authenticate({
          magic_links_token: token,
          session_duration_minutes: sessionDurationMinutes,
        });

        redirectAuthenticatedUser();
        commitPreferredSignInMethod();
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e),
        });
      }
    },
    [stytch, redirectAuthenticatedUser, pushNotification, commitPreferredSignInMethod],
  );

  const handlePasswordResetRedirect = useCallback((token: string) => {
    // Store the password reset token and show the reset password form
    // After successful reset, we'll get an intermediate token and continue with discovery
    setPasswordResetToken(token);
  }, []);

  const handleSSOAuthenticate = useCallback(
    async (token: string) => {
      try {
        await stytch.sso.authenticate({
          sso_token: token,
          session_duration_minutes: sessionDurationMinutes,
        });
        commitPreferredSignInMethod();
        redirectAuthenticatedUser();
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [stytch, redirectAuthenticatedUser, pushNotification, commitPreferredSignInMethod],
  );

  const authenticate = useCallback(async () => {
    setIsLoading(true);

    switch (tokenType) {
      case 'discovery':
        // Check if this is a password reset redirect
        if (redirectType === 'reset_password') {
          clearURLSearchParams();
          handlePasswordResetRedirect(token!);
        } else {
          clearURLSearchParams();
          await handleDiscovery(token!);
        }
        break;
      case 'discovery_oauth':
        clearURLSearchParams();
        await handleOAuthDiscovery(token!);
        break;
      case 'multi_tenant_magic_links':
        clearURLSearchParams();
        await handleMultiTenantMagicLinks(token!);
        break;
      case 'sso':
        clearURLSearchParams();
        await handleSSOAuthenticate(token!);
        break;
    }

    setIsLoading(false);
  }, [
    clearURLSearchParams,
    handleDiscovery,
    handleOAuthDiscovery,
    handleMultiTenantMagicLinks,
    handlePasswordResetRedirect,
    handleSSOAuthenticate,
    token,
    tokenType,
    redirectType,
  ]);

  useEffect(() => {
    // Skip if already run or if this is a tenant-specific redirect (handled by Navigate)
    if (hasRun.current || slug) {
      return;
    }

    if (token && tokenType && !isLoading && !discoveryInfo) {
      hasRun.current = true;
      // async auth sets state on completion
      // eslint-disable-next-line react-hooks/set-state-in-effect
      authenticate();
    }
  }, [authenticate, isLoading, discoveryInfo, token, tokenType, slug]);

  // Redirect to tenant sign-in page for org-specific auth flows
  if (slug) {
    return <Navigate to={ROUTES.signInInstanceBySlug(slug, searchParams)} replace />;
  }

  if (discoveryInfo) {
    return <Navigate to={ROUTES.signInDiscovery.path} state={{ ...discoveryInfo }} replace />;
  }

  if (passwordResetToken) {
    return <ResetPasswordDiscovery token={passwordResetToken} />;
  }

  return <>{isLoading ? <PageSpinner /> : children}</>;
};

export default DiscoveryAuthenticate;
